/* eslint-disable no-unused-vars */
import {React, lazy, useEffect, useState} from 'react';
import PrivateRoute from './route/PrivateRoute';
import Layout from './layout/Index';
import useIdleTimer from './utils/useIdleTimer';
import IdleWarning from './components/IdleWarning';
//import {withAuthenticationRequired} from '@auth0/auth0-react';
import Login from './pages/auth/Login.jsx';
import CompanySelection from './pages/CompanySelection.jsx';
import { clearCompanySelection, hasSelectedCompany } from './utils/companyUtils';
import CompanyProtectedRoute from './components/CompanyProtectedRoute';
import './components/modal/CompanySelector.css';

// 🚀 IMPORTAR INTERCEPTOR GLOBAL - Configura automáticamente X-Database header en todos los fetch
import './utils/fetchInterceptor';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {Switch, Route, Redirect} from 'react-router-dom';
import {RedirectAs404} from './utils/Utils';
import { Ge } from 'react-flags-select';

const Faq = lazy(() => import('./pages/others/Faq'));
const Terms = lazy(() => import('./pages/others/Terms'));

const Error404Classic = lazy(() =>
  import('./pages/error/404-classic')
);
const Error404Modern = lazy(() => import('./pages/error/404-modern'));
const Error504Modern = lazy(() => import('./pages/error/504-modern'));
const Error504Classic = lazy(() =>
  import('./pages/error/504-classic')
);
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() =>
  import('./pages/auth/ForgotPassword')
);
const Success = lazy(() => import('./pages/auth/Success'));
const InvoicePrint = lazy(() =>
  import('./pages/pre-built/invoice/InvoicePrint')
);

const DetallesdelSeller = lazy(() =>
  import('./listgsa/components/detallesdelseller')
);
const DetailOrder = lazy(() =>
  import('./plasticos/components/detailorder')
);
const PlasticOrigin = lazy(() =>
  import('./dashimpo/components/plasticorigin')
);

const ExpoDetailOrder = lazy(() => import('./Exportation/followUp/followUpExpoDetail'));
// Automatizations Gestor
const AutomatizationsGestor = lazy(() => import('./automatizaciones/AutomatizationsGestor'));
// Data Management
const DataUpsert = lazy(() => import('./dataManagement/DataUpsert'));
const SerialesCilindrosPage = lazy(() => import('./Importer/cilindros/pages/SerialesCilindrosPage'));
const TemporalesPage = lazy(() => import('./Importer/temporales/pages/TemporalesPage'));
const TransitosPage = lazy(() => import('./Importer/transitos/pages/TransitosPage'));
const GestionPage = lazy(() => import('./Importer/gestion/pages/GestionPage'));
const ConsumosPage = lazy(() => import('./Importer/consumo_transitos/pages/ConsumosPage'));
const GestionRetPage = lazy(() => import('./Importer/gestion_ret/pages/GestionRetPage'));

const isAuthenticated = () => !!localStorage.getItem('auth_token');
const isCompanySelected = () => hasSelectedCompany();

// Componente que protege Layout con validación completa
const ProtectedLayout = (props) => {
  if (!isAuthenticated()) {
    return <Redirect to="/auth-login" />;
  } else if (!isCompanySelected()) {
    return <Redirect to="/company-selection" />;
  } else {
    return <Layout {...props} />;
  }
};

const App = () => {
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  
  // Función para cerrar sesión por inactividad
  const handleIdleLogout = async () => {
    if (isAuthenticated()) {
      try {
        // Limpiar selección de empresa
        await clearCompanySelection();
      } catch (error) {
        console.error('Error clearing company on idle logout:', error);
      } finally {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user");
        localStorage.removeItem("lastActivity");
        setShowIdleWarning(false);
        window.location.href = `${process.env.PUBLIC_URL}/auth-login`;
      }
    }
  };

  // Función para mostrar advertencia de inactividad
  const handleIdleWarning = () => {
    if (isAuthenticated()) {
      setShowIdleWarning(true);
    }
  };

  // Función para continuar sesión
  const handleContinueSession = () => {
    setShowIdleWarning(false);
    localStorage.setItem("lastActivity", Date.now().toString());
    resetTimer(); // Resetear el timer de inactividad
  };

  // Hook para detectar inactividad (2 horas = 2 * 60 * 60 * 1000 ms)
  // Advertencia 5 minutos antes (5 * 60 * 1000 ms)
  const { resetTimer, isWarningActive, remainingTime } = useIdleTimer(
    2 * 60 * 60 * 1000, // 2 horas total
    handleIdleLogout,    // callback al expirar
    5 * 60 * 1000,      // advertencia 5 minutos antes
    handleIdleWarning    // callback de advertencia
  );
  
  // Verificación automática de expiración de token Y inactividad
  useEffect(() => {
    const checkTokenAndActivity = () => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        try {
          // Verificar expiración del JWT
          const payload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Date.now() / 1000;
          
          if (payload.exp <= currentTime) {
            localStorage.removeItem("auth_token");
            localStorage.removeItem("user");
            localStorage.removeItem("lastActivity");
            window.location.href = `${process.env.PUBLIC_URL}/auth-login`;
            return;
          }
          
          // Verificar tiempo de inactividad (2 horas = 7200000 ms)
          const lastActivity = localStorage.getItem("lastActivity");
          if (lastActivity) {
            const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
            const maxInactiveTime = 2 * 60 * 60 * 1000; // 2 horas
            
            if (timeSinceLastActivity > maxInactiveTime) {
              localStorage.removeItem("auth_token");
              localStorage.removeItem("user");
              localStorage.removeItem("lastActivity");
              window.location.href = `${process.env.PUBLIC_URL}/auth-login`;
              return;
            }
          } else {
            // Si no hay lastActivity, establecerlo ahora
            localStorage.setItem("lastActivity", Date.now().toString());
          }
          
        } catch (error) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user");
          localStorage.removeItem("lastActivity");
        }
      }
    };
    
    // Verificar inmediatamente al cargar
    checkTokenAndActivity();
    
    // Verificar cada minuto (más frecuente para múltiples pestañas)
    const interval = setInterval(checkTokenAndActivity, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Actualizar actividad en eventos de usuario
  useEffect(() => {
    const updateLastActivity = () => {
      if (isAuthenticated()) {
        localStorage.setItem("lastActivity", Date.now().toString());
      }
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateLastActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateLastActivity, true);
      });
    };
  }, []);

  // Escuchar cambios en localStorage desde otras pestañas
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'auth_token' && !e.newValue) {
        // Si otra pestaña cerró la sesión, cerrar esta también
        setShowIdleWarning(false);
        window.location.href = `${process.env.PUBLIC_URL}/auth-login`;
      }
      if (e.key === 'lastActivity' && e.newValue) {
        // Si otra pestaña actualizó la actividad, resetear timer local
        resetTimer();
        setShowIdleWarning(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [resetTimer]);

  return (
    <>
      <Switch>
         <Route
          exact
          path={`${process.env.PUBLIC_URL}/auth-login`}
          component={Login}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/company-selection`}
          component={CompanySelection}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/auth-success`}
          component={Success}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/auth-reset`}
          component={ForgotPassword}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/auth-register`}
          component={Register}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/invoice-print/:id`}
          component={InvoicePrint}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/auths/terms`}
          component={Terms}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/auths/faq`}
          component={Faq}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/invoice-print`}
          component={InvoicePrint}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/errors/404-classic`}
          component={Error404Classic}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/errors/504-modern`}
          component={Error504Modern}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/errors/404-modern`}
          component={Error404Modern}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/errors/504-classic`}
          component={Error504Classic}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/detalleseller/:id`}
          render={(props) => <DetallesdelSeller {...props} />}
          component={ProtectedLayout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/detailorder/:id`}
          render={(props) => {
            const token = localStorage.getItem('auth_token');
            if (!token) {
              return <Redirect to="/auth-login" />;
            }
            if (!isCompanySelected()) {
              return <Redirect to="/company-selection" />;
            }
            return (
              <Layout>
                <DetailOrder {...props} />
              </Layout>
            );
          }}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/detallesorders/:id`}
          render={(props) => <PlasticOrigin {...props} />}
          component={ProtectedLayout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/calculator`}
          component={ProtectedLayout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/listgsa`}
          component={ProtectedLayout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/followup`}
          component={Layout}
        ></Route>

        {/* test */}
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/containerExport`}
          component={Layout}
        ></Route>
        {/*  */}

        {/* test */}
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/follow-up-report`}
          component={Layout}
        ></Route>
        {/* user management */}
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/user_management`}
          component={Layout}
        ></Route>
        {/*  */}

        {/* MacroEdit */}
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/macroUpdate`}
          component={Layout}
        ></Route>
        {/*  */}

        <Route
          exact
          path={`${process.env.PUBLIC_URL}/powerbi`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/dashimpo`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/reportImpo`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/followUpExpo`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/followUpExpoDemo`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/reportExpo`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/dashboardExpo`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/expo-detailOrder/:po`}
          render={(props) => {
            const token = localStorage.getItem('auth_token');
            if (!token) {
              return <Redirect to="/auth-login" />;
            }
            if (!isCompanySelected()) {
              return <Redirect to="/company-selection" />;
            }
            return (
              <Layout>
                <ExpoDetailOrder {...props} />
              </Layout>
            );
          }}
        ></Route>

        {/* Automatizations Gestor acceso directo */}
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/automatizations-gestor`}
          render={(props) => {
            const token = localStorage.getItem('auth_token');
            if (!token) {
              return <Redirect to="/auth-login" />;
            }
            if (!isCompanySelected()) {
              return <Redirect to="/company-selection" />;
            }
            return (
              <Layout>
                <AutomatizationsGestor {...props} />
              </Layout>
            );
          }}
        ></Route>

        {/* Data Management - Upsert (solo Bostik) */}
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/data-upsert`}
          render={(props) => {
            const token = localStorage.getItem('auth_token');
            if (!token) {
              return <Redirect to="/auth-login" />;
            }
            if (!isCompanySelected()) {
              return <Redirect to="/company-selection" />;
            }
            return (
              <Layout>
                <DataUpsert {...props} />
              </Layout>
            );
          }}
        ></Route>

        <Route
          exact
          path={`${process.env.PUBLIC_URL}/seriales-cilindros`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/temporales`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/transitos`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/gestion`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/consumo-transitos`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/gestion-ret`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/json-304`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/bots`}
          component={Layout}
        ></Route>

        <Route
          exact
          path={`${process.env.PUBLIC_URL}/transportation-method`}
          component={Layout}
        ></Route>

        {/* demo new order*/}
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/newOrder`}
          component={Layout}
        ></Route>
        {/* demo new order*/}
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/expo-detailOrderDemo/:po`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/arrivals`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/arrivals2`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/dragreport`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/dragreport/:reportId`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/reportList`}
          component={Layout}
        ></Route>
         <Route
          exact
          path={`${process.env.PUBLIC_URL}/muestras`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/viewCustomReport/:reportId`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/dragreportexport`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/expo/master-db`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/subir-excel`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/dragreportexport/:reportId`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/help-documents`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/exporeportlist`}
          component={Layout}
        ></Route>
        <Route
          exact
          path={`${process.env.PUBLIC_URL}/viewExpoCustomReport/:reportId`}
          component={Layout}
        ></Route>
        <Route
          path="/"
          render={(props) => {
            if (!isAuthenticated()) {
              return <Redirect to="/auth-login" />;
            } else if (!isCompanySelected()) {
              return <Redirect to="/company-selection" />;
            } else {
              return <Layout {...props} />;
            }
          }}
        />
        {/*<PrivateRoute exact path="" component={Layout}></PrivateRoute>*/}
        <Route component={RedirectAs404}></Route>
      </Switch>
      
      {/* Modal de advertencia de inactividad */}
      <IdleWarning
        isVisible={showIdleWarning}
        onContinue={handleContinueSession}
        onLogout={handleIdleLogout}
        remainingTime={remainingTime}
      />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
};
export default /*withAuthenticationRequired(App)*/App;
